/**
 * Minimum order validation for cart page
 * Ensures checkout button is disabled when cart total is below $500
 */

(function() {
  'use strict';

  const DEFAULT_MINIMUM_ORDER_CENTS = 50000; // $500 fallback

  function checkMinimumOrder() {
    // Check if customer is B2B - only B2B customers can checkout
    const b2bCheckEl = document.querySelector('.theme__b2b-check');
    const isB2B = b2bCheckEl && b2bCheckEl.dataset.isB2b === 'true';
    
    // If not B2B, hide minimum order message and disable checkout
    if (!isB2B) {
      const messageEl = document.getElementById('cart-minimum-order-message');
      if (messageEl) {
        messageEl.hidden = true;
      }
      
      // Disable checkout buttons for non-B2B customers
      const checkoutButtons = document.querySelectorAll('button[name="checkout"]');
      checkoutButtons.forEach((button) => {
        button.disabled = true;
        button.classList.add('btn--disabled');
      });
      
      // Hide additional checkout buttons
      const additionalCheckoutButtons = document.querySelector('.dynamic-cart-btns');
      if (additionalCheckoutButtons) {
        additionalCheckoutButtons.style.display = 'none';
      }
      
      return;
    }
    
    // Get checkout buttons - read staff discount status from button data attributes (set by Liquid)
    const checkoutButtons = document.querySelectorAll('button[name="checkout"]');
    
    if (checkoutButtons.length === 0) {
      return;
    }
    
    // Resolve minimum order from dynamic override data if available.
    const firstButton = checkoutButtons[0];
    const minimumFromB2b = b2bCheckEl ? b2bCheckEl.dataset.minimumOrderCents : null;
    const minimumOrderCents = parseInt(
      minimumFromB2b || firstButton.dataset.minimumOrderCents || DEFAULT_MINIMUM_ORDER_CENTS,
      10
    );
    
    // Value the minimum-order rule is measured against. Prefer data-mov-cents
    // (the Subtotal: Total − promo, BEFORE any account-credit voucher) so an
    // applied credit voucher — which is tender, not a discount — can never push
    // the order under the minimum and block checkout. Falls back to the cart
    // total for carts/buttons that don't expose the MOV value.
    let cartTotalCents = 0;
    const movAttr = firstButton.dataset.movCents;
    const priceAttr = firstButton.dataset.cartTotal;
    if (movAttr) {
      cartTotalCents = parseInt(movAttr, 10);
    } else if (priceAttr) {
      cartTotalCents = parseInt(priceAttr, 10);
    }

    // Calculate remaining amount for message (only if not staff discount)
    const remainingCents = Math.max(0, minimumOrderCents - cartTotalCents);
    
    // Format remaining amount
    let remainingFormatted = `$${(remainingCents / 100).toFixed(2)}`;
    if (window.theme && window.theme.moneyFormat) {
      remainingFormatted = window.theme.moneyFormat.replace('{{amount}}', (remainingCents / 100).toFixed(2));
    }

    const meetsMinimum = cartTotalCents >= minimumOrderCents;

    // Update all checkout buttons
    checkoutButtons.forEach((button) => {
      const isEcomBlocked = button.dataset.ecomBlocked === 'true';
      button.dataset.minimumOrderCents = String(minimumOrderCents);
      button.dataset.meetsMinimum = String(meetsMinimum);

      if (isEcomBlocked) {
        // Always keep disabled if ecom is blocked
        button.disabled = true;
        button.classList.add('btn--disabled');
      } else if (meetsMinimum) {
        // Enable button if minimum is met
        button.disabled = false;
        button.classList.remove('btn--disabled');
      } else {
        button.disabled = true;
        button.classList.add('btn--disabled');
      }
    });

    // Update minimum order message (check both cart page and cart drawer)
    let messageEl = document.getElementById('cart-minimum-order-message');
    if (!messageEl) {
      // Try to find message in cart drawer
      const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
      if (cartDrawer) {
        messageEl = cartDrawer.querySelector('#cart-minimum-order-message');
      }
    }
    
    if (!messageEl) {
      // Create message element if it doesn't exist
      messageEl = document.createElement('div');
      messageEl.id = 'cart-minimum-order-message';
      messageEl.className = 'alert mt-4 mb-4 bg-warning-bg text-warning-text';
      messageEl.setAttribute('role', 'alert');

      // Insert before checkout button
      const checkoutContainer = document.querySelector('.cart__summary, .cart-drawer__checkout-buttons');
      if (checkoutContainer) {
        const checkoutButton = checkoutContainer.querySelector('button[name="checkout"]');
        if (checkoutButton) {
          checkoutButton.parentNode.insertBefore(messageEl, checkoutButton);
        } else {
          checkoutContainer.insertBefore(messageEl, checkoutContainer.firstChild);
        }
      }
    }

    // Hide message if minimum is met.
    if (meetsMinimum) {
      if (messageEl) {
        messageEl.hidden = true;
      }
    } else {
      if (messageEl) {
        messageEl.hidden = false;
        // Use translation string if available
        const messageText = (window.theme && window.theme.strings && window.theme.strings.cartMinimumOrder) || 
          `Minimum order required. Add ${remainingFormatted} more to checkout.`;
        messageEl.textContent = messageText.replace('[amount]', remainingFormatted);
      }
    }

    // Hide additional checkout buttons if minimum not met
    const additionalCheckoutButtons = document.querySelector('.dynamic-cart-btns');
    if (additionalCheckoutButtons) {
      additionalCheckoutButtons.dataset.meetsMinimum = String(meetsMinimum);
      const meetsMinimumFromButtons = meetsMinimum;

      if (meetsMinimumFromButtons) {
        additionalCheckoutButtons.style.display = '';
      } else {
        additionalCheckoutButtons.style.display = 'none';
      }
    }

    // Allow clearance validation to override button state.
    if (typeof window.checkClearanceValidation === 'function') {
      window.checkClearanceValidation();
    }
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(checkMinimumOrder, 100);
    });
  } else {
    setTimeout(checkMinimumOrder, 100);
  }

  // Also run when cart is updated (listen for custom events or DOM mutations)
  document.addEventListener('cart:updated', checkMinimumOrder);
  document.addEventListener('company-order-override:updated', checkMinimumOrder);
  
  // Debounce checkMinimumOrder to prevent excessive calls
  let checkMinimumOrderTimeout = null;
  const debouncedCheckMinimumOrder = () => {
    if (checkMinimumOrderTimeout) {
      clearTimeout(checkMinimumOrderTimeout);
    }
    checkMinimumOrderTimeout = setTimeout(() => {
      checkMinimumOrder();
      checkMinimumOrderTimeout = null;
    }, 150);
  };

  // Watch for cart drawer opening and content updates
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.addEventListener('cart:open', debouncedCheckMinimumOrder);
    
    // Watch for when drawer becomes visible (simplified observer)
    const drawerObserver = new MutationObserver(function(mutations) {
      let shouldCheck = false;
      mutations.forEach(function(mutation) {
        // Only check on aria-hidden changes (drawer open/close)
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const isHidden = cartDrawer.getAttribute('aria-hidden') === 'true';
          if (!isHidden) {
            shouldCheck = true;
          }
        }
      });
      if (shouldCheck) {
        debouncedCheckMinimumOrder();
      }
    });
    drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }
  
  // Watch for changes to cart total elements (simplified, debounced observer)
  const observer = new MutationObserver(function(mutations) {
    let shouldCheck = false;
    mutations.forEach(function(mutation) {
      // Only check on relevant attribute changes
      if (mutation.type === 'attributes' && 
          (mutation.attributeName === 'data-cart-total' || 
           mutation.attributeName === 'data-price' ||
           mutation.attributeName === 'data-is-staff-discount' ||
           mutation.attributeName === 'data-meets-minimum')) {
        shouldCheck = true;
      }
      // Only check childList changes if checkout buttons are involved
      if (mutation.type === 'childList' && mutation.target) {
        const target = mutation.target;
        if (target.querySelector && target.querySelector('button[name="checkout"]')) {
          shouldCheck = true;
        }
      }
    });
    if (shouldCheck) {
      debouncedCheckMinimumOrder();
    }
  });

  // Observe cart summary for changes (only checkout area)
  const cartSummary = document.querySelector('.cart__summary');
  if (cartSummary) {
    const checkoutArea = cartSummary.querySelector('.cart__checkout-buttons, [form="cart"]');
    if (checkoutArea) {
      observer.observe(checkoutArea, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-total', 'data-price', 'data-is-staff-discount', 'data-meets-minimum']
      });
    }
  }
  
  // Also observe cart drawer content - only checkout button area
  if (cartDrawer) {
    const checkoutArea = cartDrawer.querySelector('.cart-drawer__checkout-buttons, .cart-drawer__footer, [form="cart-drawer-form"]');
    if (checkoutArea) {
      observer.observe(checkoutArea, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-total', 'data-is-staff-discount', 'data-meets-minimum', 'disabled']
      });
    }
  }

  // Expose checkMinimumOrder globally so other scripts can call it
  window.checkMinimumOrder = checkMinimumOrder;
})();

